// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { isNeutralizationTheoremCode, witnessKindForTheorem } from "@csk/core";
import { WASM_THEOREM_META } from "./meta.js";
export const DEFAULT_SNF_TRACE = [
    "B1",
    "B2",
    "B3",
    "B4",
];
const BRIDGE_ADMISSION_STATUSES = new Set([
    "admitted",
    "rejected-missing-witness",
    "rejected-forbidden-square",
    "rejected-invalid-lifecycle",
]);
const REJECTED_BRIDGE_ADMISSION_STATUSES = new Set([
    "rejected-missing-witness",
    "rejected-forbidden-square",
    "rejected-invalid-lifecycle",
]);
const STATEFUL_BRIDGE_LIFECYCLES = new Set([
    "proposed",
    "admitted",
    "rejected",
]);
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
function isSafeId(value) {
    return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}
function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
}
function isSnfTracePhases(value) {
    return (Array.isArray(value) &&
        value.length === DEFAULT_SNF_TRACE.length &&
        DEFAULT_SNF_TRACE.every((phase, index) => value[index] === phase));
}
function isConsumedResourceList(value) {
    return Array.isArray(value) && value.every(isNonEmptyString);
}
function hasExpectedConsumedResources(resources, expected) {
    if (expected.witnessId === null)
        return false;
    return (resources.length === 2 &&
        resources[0] === `seam:${expected.seamId.toString()}` &&
        resources[1] === `witness:${expected.witnessId.toString()}`);
}
function isBridgeAdmissionStatus(value) {
    return (typeof value === "string" &&
        BRIDGE_ADMISSION_STATUSES.has(value));
}
function isRejectedAdmissionStatus(value) {
    return value !== "admitted" && REJECTED_BRIDGE_ADMISSION_STATUSES.has(value);
}
function hasExpectedAnchors(value, expected) {
    const nextAnchor = value["nextAnchor"];
    return (value["previousAnchor"] === null &&
        (nextAnchor === null ||
            nextAnchor === `bridge:${expected.bridgeId.toString()}:receipt`));
}
function isBridgeAdmissionReceipt(value, expected) {
    if (!isRecord(value))
        return false;
    const theoremCode = value["theoremCode"];
    const witnessKind = value["witnessKind"];
    const consumedResources = value["consumedResources"];
    if (theoremCode !== expected.theoremCode ||
        !isNeutralizationTheoremCode(theoremCode) ||
        witnessKind !== witnessKindForTheorem(theoremCode) ||
        expected.witnessId === null ||
        value["bridgeId"] !== expected.bridgeId ||
        value["seamId"] !== expected.seamId ||
        value["witnessId"] !== expected.witnessId ||
        value["source"] !== expected.source ||
        value["target"] !== expected.target ||
        (expected.scope !== null && value["scope"] !== expected.scope)) {
        return false;
    }
    return (isSafeId(value["bridgeId"]) &&
        isSafeId(value["seamId"]) &&
        isSafeId(value["witnessId"]) &&
        isNonEmptyString(value["scope"]) &&
        value["lifecycle"] === "admitted" &&
        isSnfTracePhases(value["phases"]) &&
        isConsumedResourceList(consumedResources) &&
        hasExpectedConsumedResources(consumedResources, expected) &&
        hasExpectedAnchors(value, expected));
}
export function defaultForbiddenPattern(theoremCode) {
    const meta = WASM_THEOREM_META[theoremCode];
    if (meta === undefined) {
        throw new Error(`No WASM theorem metadata for ${theoremCode}`);
    }
    return `${meta.generator}:${meta.name}`;
}
export function requireNonEmptyInput(value, name) {
    const trimmed = value.trim();
    if (trimmed.length === 0)
        throw new Error(`${name} cannot be empty`);
    return trimmed;
}
export function requirePositiveInteger(value, name) {
    if (!Number.isFinite(value) || !Number.isSafeInteger(value) || value < 1) {
        throw new Error(`${name} must be a safe integer >= 1`);
    }
    return value;
}
export function parseBridgeAdmissionOutcome(serialized, expected) {
    const value = JSON.parse(serialized);
    const status = isRecord(value) ? value["status"] : undefined;
    if (!isRecord(value) || !isBridgeAdmissionStatus(status)) {
        throw new Error("Invalid bridge admission outcome from WASM backend");
    }
    const receipt = value["receipt"];
    if (value["bridgeId"] !== expected.bridgeId) {
        throw new Error("Invalid bridge admission outcome from WASM backend");
    }
    if (status === "admitted") {
        if (value["reason"] !== null ||
            !isBridgeAdmissionReceipt(receipt, expected)) {
            throw new Error("Invalid bridge admission outcome from WASM backend");
        }
        return {
            status,
            bridgeId: expected.bridgeId,
            reason: null,
            receipt,
        };
    }
    if (!isRejectedAdmissionStatus(status) ||
        !isNonEmptyString(value["reason"]) ||
        receipt !== null) {
        throw new Error("Invalid bridge admission outcome from WASM backend");
    }
    return {
        status,
        bridgeId: expected.bridgeId,
        reason: value["reason"],
        receipt: null,
    };
}
export function parseBridgeAdmissionReceipt(serialized, expected) {
    const value = JSON.parse(serialized);
    if (!isBridgeAdmissionReceipt(value, expected)) {
        throw new Error("Invalid bridge receipt from WASM backend");
    }
    return value;
}
export function parseBridgeLifecycle(value) {
    if (STATEFUL_BRIDGE_LIFECYCLES.has(value)) {
        return value;
    }
    throw new Error("Invalid bridge lifecycle from WASM backend");
}
export function parseRemainingWitnessUses(value) {
    if (!Number.isFinite(value) || !Number.isSafeInteger(value) || value < 0) {
        throw new Error("Invalid witness use count from WASM backend");
    }
    return value;
}
//# sourceMappingURL=seam-bridge-adapter-validation.js.map